import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ClipboardList, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LISTS = [
  { id: 'air-academy', label: 'Air Academy' },
  { id: 'youth', label: 'Youth Survey' },
  { id: 'afterschool', label: 'Adventure Club (legacy)' },
];

const formatWhen = (iso) => iso
  ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '';

const SKIP_KEYS = new Set(['email', 'parent_name', 'parent_first_name', 'parent_last_name', 'created_at', 'updated_at', 'status', 'source', 'recaptcha_score']);

const displayName = (e) => e.parent_name
  || [e.parent_first_name, e.parent_last_name].filter(Boolean).join(' ')
  || '—';

const detailPairs = (entry) => Object.entries(entry)
  .filter(([k, v]) => !SKIP_KEYS.has(k) && v && (!Array.isArray(v) || v.length))
  .map(([k, v]) => [k.replace(/_/g, ' '), Array.isArray(v) ? v.join(', ') : String(v)]);

const InterestTab = () => {
  const [list, setList] = useState('air-academy');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(null);

  const fetchList = useCallback(async (which) => {
    setLoading(true);
    setError('');
    setExpanded(null);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/admin/interest-lists/${which}`, { withCredentials: true });
      setEntries(data);
    } catch (err) {
      setError(err.response?.status === 401 || err.response?.status === 403
        ? 'Not authorized — please log in as admin.'
        : 'Could not load this list. Try again shortly.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(list); }, [list, fetchList]);

  return (
    <div className="chat-tab" data-testid="admin-interest-tab">
      <div className="chat-tab-header">
        <div>
          <h2><ClipboardList size={22} /> Interest Lists</h2>
          <p className="chat-tab-sub">Parent signups and survey responses for developing programs. Click a row to see full answers.</p>
        </div>
        <button className="chat-refresh-btn" onClick={() => fetchList(list)} data-testid="interest-refresh-btn">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {LISTS.map(l => (
          <button
            key={l.id}
            className="chat-refresh-btn"
            style={list === l.id ? { background: '#1A1A1A', color: '#fff', borderColor: '#1A1A1A' } : {}}
            onClick={() => setList(l.id)}
            data-testid={`interest-list-${l.id}`}
          >
            {l.label}
          </button>
        ))}
      </div>

      {loading && <p className="chat-tab-status">Loading…</p>}
      {error && <p className="chat-tab-status chat-tab-error">{error}</p>}
      {!loading && !error && entries.length === 0 && (
        <p className="chat-tab-status">No submissions in this list yet.</p>
      )}

      {!loading && !error && entries.length > 0 && (
        <div className="spam-log-table" data-testid="interest-entries-table">
          <div className="spam-log-row spam-log-head">
            <span>Parent</span>
            <span>Email</span>
            <span>Submitted</span>
          </div>
          {entries.map((e, i) => (
            <React.Fragment key={`${e.email}-${i}`}>
              <div
                className="spam-log-row"
                style={{ cursor: 'pointer' }}
                onClick={() => setExpanded(expanded === i ? null : i)}
                data-testid={`interest-row-${i}`}
              >
                <span>{displayName(e)}</span>
                <span className="spam-email">{e.email || '—'}</span>
                <span>{formatWhen(e.created_at || e.subscribed_at)}</span>
              </div>
              {expanded === i && (
                <div style={{ padding: '12px 16px', background: '#FDFBF7', borderRadius: '8px', margin: '4px 0 10px', fontSize: '14px' }} data-testid={`interest-detail-${i}`}>
                  {detailPairs(e).length === 0 && <em>No additional answers.</em>}
                  {detailPairs(e).map(([k, v]) => (
                    <p key={k} style={{ margin: '4px 0' }}>
                      <strong style={{ textTransform: 'capitalize' }}>{k}:</strong> {v}
                    </p>
                  ))}
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default InterestTab;
